// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconTextLinkArrowLeftInactive from "@tps/XDL/Icons/IconTextLinkArrowLeftInactive";
import { xScreen } from "@tps/XDL/Utilities";
import { iconLinkCommonStyles } from "@tps/XDL/Utilities";
import { css } from "styled-components";

export interface CartLinesWarehouseStyles {
    wrapper?: InjectableCss;
    warehouseGroup?: InjectableCss;
    warehouseFooter?: InjectableCss;
    warehouseSubTotalWeightWrapper?: InjectableCss;
    warehouseSubTotalTypo?: TypographyPresentationProps;
    warehouseWeightTypo?: TypographyPresentationProps;
    unit?: TypographyPresentationProps;
    verifyShippingCheckbox?: CheckboxPresentationProps;
    shippingDetailsSection?: InjectableCss;
    shippingDetailsTitle?: TypographyPresentationProps;
    editOptionLink?: LinkPresentationProps;
    selectedOptionsWrapper?: InjectableCss;
    shippingOptionStyle?: SmallHeadingAndTextStyles;
}

export const cartLinesWarehouseSharedStyles: CartLinesWarehouseStyles = {
    warehouseGroup: {
        css: css`
            margin-bottom: 20px;
        `,
    },

    warehouseFooter: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: 12px;
            border: 1px solid ${getColor("common.ui4")};
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                > div {
                    width: 100%;
                }
            }
        `,
    },

    warehouseSubTotalWeightWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-end;

            ${xScreen("lte", "sm")} {
                margin-top: 16px;
            }
        `,
    },
    warehouseSubTotalTypo: {
        size: 14,
        lineHeight: "22px",
        css: css`
            margin-bottom: 4px;
        `,
    },
    warehouseWeightTypo: {
        size: 14,
        lineHeight: "22px",
    },
    unit: {
        weight: "bold",
        css: css`
            text-transform: capitalize;
        `,
    },
    verifyShippingCheckbox: {
        typographyProps: {
            size: 16,
            lineHeight: "22px",
            weight: "bold",
            color: "primary.main",
            css: css`
                margin-left: 12px;
            `,
        },
        iconProps: {
            src: "Check",
            size: 16,
            css: css`
                && {
                    width: 16px;
                    height: 16px;
                }
            `,
        },
    },
    shippingDetailsSection: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            padding-right: 16px;
        `,
    },

    shippingDetailsTitle: {
        size: 14,
        lineHeight: "22px",
        css: css`
            margin-bottom: 4px;
        `,
    },

    selectedOptionsWrapper: {
        css: css`
            display: flex;
            gap: 12px;
            margin-bottom: 12px;
        `,
    },

    shippingOptionStyle: {
        extendedStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    row-gap: 4px;
                    width: auto;
                `,
            },
            heading: {
                size: 12,
                weight: "normal",
                lineHeight: "17px",
                transform: "capitalize",
            },
            text: {
                size: 12,
                weight: "bold",
                lineHeight: "17px",
                transform: "capitalize",
            },
        },
    },
    editOptionLink: {
        ...iconLinkCommonStyles(IconTextLinkArrowLeftInactive),
    },
};

export default cartLinesWarehouseSharedStyles;
